/**
 * Benutzer-Chip der Kopfzeile und das daran hängende Profilmenü (REQ-20 §4/§5).
 *
 * Der Tooltip der Navigation ist damit in die Kopfzeile gewandert. Er benutzt
 * nicht mehr die `Tooltip`-Komponente: Deren Regeln aus dem Altclient hefteten
 * das Menü mit `!important` an die linke obere Ecke — die Lage der alten
 * Leiste (`styles/components/_profile.scss`, mit REQ-23 §3 entfallen).
 * Stattdessen sind hier `Portal`, `usePopupPosition` und `useOutsideDismiss`
 * direkt verwendet; das Menü hängt dadurch unter dem Chip.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { useAuth } from '@/auth/useAuth';
import { Portal } from '@/components/ui/Portal';
import { useOutsideDismiss, usePopupPosition } from '@/components/ui/usePopupPosition';

import { ProfileAvatar } from './ProfileAvatar';
import { ProfileMenu } from './ProfileMenu';

/** Abstand zwischen Chip und Menü, wie bei den übrigen Popups des Entwurfs. */
const POPUP_OFFSET = 6;

export function ProfileTooltip(): ReactElement {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useOutsideDismiss(open, close, [anchorRef, popupRef]);

  const popupStyle = usePopupPosition({
    open,
    anchorRef,
    popupRef,
    align: 'BottomRight',
    offset: POPUP_OFFSET,
  });

  useEffect(() => {
    if (!open) return;

    const handler = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      close();
      anchorRef.current?.focus();
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [close, open]);

  return (
    <>
      <button
        type="button"
        id="iwm-profile-chip"
        ref={anchorRef}
        className="iwm-user-chip"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <ProfileAvatar image={auth.profileImage} name={auth.user?.Name ?? null} size={30} />
        <span className="iwm-user-chip-details">
          <span className="iwm-user-chip-name">{auth.user?.Name}</span>
          <span className="iwm-user-chip-company">{auth.company}</span>
        </span>
      </button>

      {open ? (
        <Portal>
          <div ref={popupRef} className="iwm-profile-menu" style={popupStyle}>
            <ProfileMenu />
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export default ProfileTooltip;
